// Reads a person's monthly figures from a spreadsheet (the template the app gives out, or one they made themselves),
// and works out what would change in the app. Column names can be in English or Portuguese, months can be written
// almost any way ("2026-10", "Oct 2026", "outubro de 2026", "10/2026", a real date…), amounts with either decimal
// separator. Anything that can't be read becomes a warning, never a silent guess.

import { parseMoney, r2 } from './money.js';
import { isMonth, isDate, toMonth, toDate, addMonths, daysBetween, payday as paydayOf } from './calendar.js';

export const FIELDS = {
  actualNet: 'Take-home pay received',
  reimbursed: 'Expense reimbursements in it',
  actualMeal: 'Meal card received',
  extraIncome: 'Other income',
  extraSpending: 'Spent beyond budget',
  note: 'Notes',
};

const norm = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Checked in this order; the first alias that the header starts with wins
const ALIASES = [
  ['ignore', ['expected', 'esperado', 'previsto', 'estimado', 'estimate', 'payday', 'diadepagamento', 'datadepagamento', 'status', 'estado', 'gross', 'bruto', 'salariobruto', 'vencimentobruto', 'remuneracaobruta']],
  ['month', ['month', 'mes', 'mesano', 'period', 'periodo', 'date', 'data']],
  ['actualMeal', ['mealcard', 'cartaorefeicao', 'cartaoderefeicao', 'subsidiodealimentacao', 'subsidioalimentacao', 'subsidioderefeicao', 'subsidiorefeicao', 'meal', 'refeicao', 'alimentacao']],
  ['extraIncome', ['otherincome', 'extraincome', 'outrosrendimentos', 'outrasreceitas', 'receitasextra', 'rendimentosextra', 'outrasentradas', 'entradasextra']],
  ['extraSpending', ['spentbeyondbudget', 'extraspending', 'spentoverbudget', 'overbudget', 'extraspent', 'extraexpenses', 'spentmore', 'gastosextra', 'gastosacima', 'despesasextra', 'gastoalemdoorcamento', 'gastosalemdoorcamento', 'alemdoorcamento', 'acimadoorcamento']],
  ['reimbursed', ['expensereimbursements', 'reimbursements', 'reimbursement', 'reimbursed', 'reembolsos', 'reembolso', 'despesasreembolsadas', 'ajudasdecusto']],
  ['note', ['notes', 'note', 'notas', 'nota', 'observacoes', 'observacao', 'obs', 'comments', 'comment', 'comentarios', 'comentario']],
  ['actualNet', ['takehomepayreceived', 'takehome', 'netpayreceived', 'netreceived', 'netpay', 'netsalary', 'salarioliquido', 'vencimentoliquido', 'liquidorecebido', 'recebido', 'received', 'liquido', 'salario', 'ordenado', 'vencimento', 'salary', 'net']],
];

export function fieldForHeader(text) {
  const n = norm(text);
  if (!n) return null;
  for (const [field, list] of ALIASES) if (list.some((a) => n === a || n.startsWith(a))) return field;
  return null;
}

const MONTHS = {
  jan: 1, january: 1, janeiro: 1,
  feb: 2, february: 2, fev: 2, fevereiro: 2,
  mar: 3, march: 3, marco: 3,
  apr: 4, april: 4, abr: 4, abril: 4,
  may: 5, mai: 5, maio: 5,
  jun: 6, june: 6, junho: 6,
  jul: 7, july: 7, julho: 7,
  aug: 8, august: 8, ago: 8, agosto: 8,
  sep: 9, sept: 9, september: 9, set: 9, setembro: 9,
  oct: 10, october: 10, out: 10, outubro: 10,
  nov: 11, november: 11, novembro: 11,
  dec: 12, december: 12, dez: 12, dezembro: 12,
};

const year4 = (y) => (y.length === 2 ? 2000 + Number(y) : Number(y));
const ok = (y, m) => (y >= 1990 && y <= 2100 && m >= 1 && m <= 12 ? toMonth(y, m) : null);

export function parseMonthCell(cell) {
  if (cell === null || cell === undefined) return null;
  const c = typeof cell === 'object' ? cell : { v: cell, t: typeof cell === 'number' ? 'n' : 's' };
  if (c.t === 'd' && typeof c.v === 'string') return isMonth(c.v.slice(0, 7)) ? c.v.slice(0, 7) : null;
  if (typeof c.v === 'number') {
    // An Excel date that lost its formatting (between 1990 and 2100)
    if (c.v > 32874 && c.v < 73051) {
      const d = new Date(Date.UTC(1899, 11, 30) + Math.round(c.v) * 864e5);
      return ok(d.getUTCFullYear(), d.getUTCMonth() + 1);
    }
    return null;
  }
  const s = String(c.v).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  if (!s) return null;
  let m;
  if ((m = s.match(/^(\d{4})\s*[-/.]\s*(\d{1,2})$/))) return ok(+m[1], +m[2]);
  if ((m = s.match(/^(\d{1,2})\s*[-/.]\s*(\d{4})$/))) return ok(+m[2], +m[1]);
  if ((m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[ t].*)?$/))) return ok(+m[1], +m[2]);
  if ((m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/))) return ok(+m[3], +m[2]); // day/month/year
  if ((m = s.match(/^([a-z]+)\.?\s*(?:de\s+|of\s+|[-/'’,]\s*)?'?(\d{4}|\d{2})$/))) {
    const mm = MONTHS[m[1]];
    return mm ? ok(year4(m[2]), mm) : null;
  }
  if ((m = s.match(/^(\d{4})\s*[-/]?\s*([a-z]+)\.?$/))) {
    const mm = MONTHS[m[2]];
    return mm ? ok(+m[1], mm) : null;
  }
  return null;
}

function cellValue(cell, field) {
  if (cell === null || cell === undefined) return { empty: true };
  if (field === 'note') {
    const t = typeof cell.v === 'number' ? String(cell.v) : String(cell.v ?? '').trim();
    return t ? { value: t.slice(0, 2000) } : { empty: true };
  }
  if (cell.t === 'e') return { error: `the cell shows an error (${cell.v})` };
  if (cell.t === 'b') return { error: 'expected an amount, found TRUE/FALSE' };
  if (typeof cell.v === 'number') return { value: r2(cell.v) };
  const text = String(cell.v ?? '').trim();
  if (!text || text === '-' || text === '—') return { empty: true };
  const p = parseMoney(text);
  return p.ok ? { value: p.value } : { error: `"${text}" isn't an amount` };
}

function findHeader(rows) {
  for (let r = 0; r < Math.min(rows.length, 20); r++) {
    const row = rows[r] || [];
    const cols = {};
    row.forEach((cell, c) => {
      if (!cell || cell.t !== 's') return;
      const f = fieldForHeader(cell.v);
      if (f && f !== 'ignore' && cols[f] === undefined) cols[f] = c;
    });
    if (cols.month !== undefined && Object.keys(cols).some((k) => k !== 'month')) return { row: r, cols, headers: Object.fromEntries(Object.entries(cols).map(([f, c]) => [f, String(row[c].v)])) };
  }
  return null;
}

/* ── The simple table: one row per money movement ─────────
   | Date | Type | Description | Amount |   Type: Salary · Other income · Extra expense
   The app turns it into months: salaries go to the month they pay for (worked out from the person's payday),
   everything else to the month of its date. */

export const LOG_TYPES = { salary: 'Salary', income: 'Other income', expense: 'Extra expense' };
export const LOG_HEADERS = ['Date', 'Type', 'Description', 'Amount'];

const LOG_ALIASES = [
  ['amount', ['amount', 'valor', 'montante', 'quantia', 'value', 'euros', 'eur', 'total']],
  ['type', ['type', 'tipo', 'kind', 'category', 'categoria']],
  ['description', ['description', 'descricao', 'what', 'oque', 'detalhe', 'details', 'detail', 'item', 'notes', 'note', 'notas', 'nota', 'obs']],
  ['date', ['date', 'data', 'dia', 'when', 'quando', 'day']],
];
const logField = (text) => {
  const n = norm(text);
  if (!n) return null;
  for (const [f, list] of LOG_ALIASES) if (list.some((a) => n === a || n.startsWith(a))) return f;
  return null;
};

export function parseType(text) {
  const n = norm(text);
  if (!n) return null;
  if (/^(sal|ordenado|vencimento|wage|pay(?!ment))/.test(n)) return 'salary';
  if (/^(extra|expense|despesa|gasto|compra|spent|spend|paid|pagamento|payment|saida)/.test(n)) return 'expense';
  if (/^(other|outr|income|rendimento|receita|entrada|gift|presente|prenda|bonus|premio|refund|reembolso|in$)/.test(n)) return 'income';
  return null;
}

export function parseDateCell(cell) {
  if (cell === null || cell === undefined) return null;
  const c = typeof cell === 'object' ? cell : { v: cell, t: typeof cell === 'number' ? 'n' : 's' };
  if (c.t === 'd' && typeof c.v === 'string') return isDate(c.v.slice(0, 10)) ? c.v.slice(0, 10) : null;
  if (typeof c.v === 'number') {
    if (c.v > 32874 && c.v < 73051) {
      const d = new Date(Date.UTC(1899, 11, 30) + Math.round(c.v) * 864e5);
      return toDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
    }
    return null;
  }
  const t = String(c.v).trim();
  let m;
  if ((m = t.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/))) { const d = toDate(+m[1], +m[2], +m[3]); return isDate(d) ? d : null; }
  if ((m = t.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/))) {
    const y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    const d = toDate(y, +m[2], +m[1]); // day/month/year
    return isDate(d) ? d : null;
  }
  // "31 Oct 2026", "31 de outubro de 2026"
  if ((m = t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().match(/^(\d{1,2})\s*(?:de\s+)?([a-z]+)\.?\s*(?:de\s+)?(\d{4})$/))) {
    const mm = MONTHS[m[2]];
    if (mm) { const d = toDate(+m[3], mm, +m[1]); return isDate(d) ? d : null; }
  }
  // Just a month: the middle of it
  const month = parseMonthCell(c);
  return month ? `${month}-15` : null;
}

function findLogHeader(rows) {
  for (let r = 0; r < Math.min(rows.length, 20); r++) {
    const row = rows[r] || [];
    const cols = {};
    row.forEach((cell, c) => {
      if (!cell || cell.t !== 's') return;
      const f = logField(cell.v);
      if (f && cols[f] === undefined) cols[f] = c;
    });
    if (cols.date !== undefined && cols.type !== undefined && cols.amount !== undefined) {
      return { row: r, cols, headers: Object.fromEntries(Object.entries(cols).map(([f, c]) => [f, String(row[c].v)])) };
    }
  }
  return null;
}

function parseLog(sheet, found) {
  const warnings = [];
  const entries = [];
  const rows = sheet.rows;
  for (let r = found.row + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const get = (f) => (found.cols[f] === undefined ? undefined : row[found.cols[f]]);
    const blank = (cell) => !cell || String(cell.v ?? '').trim() === '';
    if (['date', 'type', 'amount', 'description'].every((f) => blank(get(f)))) continue;
    const where = `Row ${r + 1}`;
    const date = parseDateCell(get('date'));
    if (!date) { warnings.push(`${where}: ${blank(get('date')) ? 'no date' : `couldn't read the date "${get('date').v}"`}; skipped`); continue; }
    const type = parseType(get('type')?.v);
    if (!type) { warnings.push(`${where}: ${blank(get('type')) ? 'no type' : `unknown type "${get('type').v}"`} (use ${Object.values(LOG_TYPES).join(', ')}); skipped`); continue; }
    const res = cellValue(get('amount'), 'amount');
    if (res.empty) { warnings.push(`${where}: no amount; skipped`); continue; }
    if (res.error) { warnings.push(`${where}: ${res.error}; skipped`); continue; }
    let amount = res.value;
    if (amount < 0) {
      if (type === 'salary') { warnings.push(`${where}: a salary can't be negative; skipped`); continue; }
      amount = -amount; // "-60" for an expense means the same as 60
    }
    if (amount === 0) continue;
    const description = blank(get('description')) ? '' : String(get('description').v).trim().slice(0, 120);
    entries.push({ date, type, description, amount: r2(amount), row: r + 1 });
  }
  return { entries, warnings };
}

// Which month a salary paid on `date` is for: the one whose payday is closest
export function salaryMonth(date, paydayRule, holidayOpts = {}) {
  const own = date.slice(0, 7);
  let best = own;
  let bestGap = Infinity;
  for (const m of [own, addMonths(own, -1), addMonths(own, 1)]) {
    const gap = Math.abs(daysBetween(paydayOf(m, paydayRule, holidayOpts), date));
    if (gap < bestGap) { best = m; bestGap = gap; }
  }
  return best;
}

// Table rows → per-month values, like the month-per-row layout gives
export function logToMonths(entries, person) {
  const byMonth = new Map();
  const holidays = { carnival: !!person?.holidays?.carnival, extra: person?.holidays?.extra || [] };
  for (const e of entries || []) {
    if (!e || !isDate(e.date)) continue;
    const month = e.type === 'salary' ? salaryMonth(e.date, person?.payday, holidays) : e.date.slice(0, 7);
    if (!byMonth.has(month)) byMonth.set(month, { month, values: {} });
    const v = byMonth.get(month).values;
    if (e.type === 'salary') v.actualNet = r2((v.actualNet || 0) + e.amount);
    else {
      const key = e.type === 'income' ? 'extraIncome' : 'extraSpending';
      (v[key] = v[key] || []).push({ id: `sheet${e.row}`, label: e.description || LOG_TYPES[e.type], amount: e.amount });
    }
  }
  return [...byMonth.values()].sort((a, b) => (a.month < b.month ? -1 : 1));
}

// Months for a person from whatever was read (either layout)
export const monthsFromImport = (parsed, person) => (parsed?.kind === 'log' ? logToMonths(parsed.entries, person) : parsed?.rows || []);

// book: { sheets: [{ name, hidden, rows }] } → { rows: [{ month, values, row }], warnings, meta, sheet, columns } or { error }
export function parseImport(book) {
  const sheets = book?.sheets || [];
  const metaSheet = sheets.find((s) => s.name === '_meta');
  const meta = {};
  if (metaSheet) for (const row of metaSheet.rows || []) if (row?.[0]?.v && row?.[1]) meta[String(row[0].v)] = row[1].v;
  if (meta.kind === 'report') {
    return { error: "This is the Excel report, which is only for reading. To send figures to the app, download that person's Template, fill it in and upload it." };
  }
  const visible = sheets.filter((s) => s.name !== '_meta');
  const ordered = [...visible.filter((s) => !s.hidden), ...visible.filter((s) => s.hidden)];
  for (const s of ordered) {
    const log = findLogHeader(s.rows || []);
    if (log) {
      const { entries, warnings } = parseLog(s, log);
      return { kind: 'log', entries, rows: [], warnings, meta, sheet: s.name, columns: log.headers };
    }
  }
  let found = null;
  let sheet = null;
  for (const s of ordered) {
    found = findHeader(s.rows || []);
    if (found) { sheet = s; break; }
  }
  if (!found) {
    return { error: "Couldn't find the columns. Use a table with Date, Type, Description and Amount columns (like the template), or one row per month with a \"Month\" column." };
  }
  const warnings = [];
  const byMonth = new Map();
  const rows = sheet.rows;
  for (let r = found.row + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const monthCell = row[found.cols.month];
    const anyValue = Object.entries(found.cols).some(([f, c]) => f !== 'month' && row[c] && String(row[c].v ?? '').trim() !== '');
    if (!monthCell || String(monthCell.v ?? '').trim() === '') {
      if (anyValue) warnings.push(`Row ${r + 1}: values without a month were skipped`);
      continue;
    }
    const month = parseMonthCell(monthCell);
    if (!month) {
      // Totals and similar summary rows are skipped quietly
      if (!/^(total|totais|sum|soma|subtotal|average|media|média)/i.test(String(monthCell.v).trim())) warnings.push(`Row ${r + 1}: couldn't read the month "${monthCell.v}"`);
      continue;
    }
    const values = {};
    for (const [field, c] of Object.entries(found.cols)) {
      if (field === 'month') continue;
      const res = cellValue(row[c], field);
      if (res.error) warnings.push(`Row ${r + 1} (${found.headers[field]}): ${res.error}`);
      else if (!res.empty) {
        if ((field === 'actualNet' || field === 'actualMeal' || field === 'reimbursed') && res.value < 0) warnings.push(`Row ${r + 1} (${found.headers[field]}): can't be negative`);
        else values[field] = res.value;
      }
    }
    if (byMonth.has(month)) warnings.push(`Row ${r + 1}: ${month} appears more than once; the last row is used`);
    byMonth.set(month, { month, values, row: r + 1 });
  }
  const out = [...byMonth.values()].sort((a, b) => (a.month < b.month ? -1 : 1));
  return { kind: 'months', rows: out, warnings, meta, sheet: sheet.name, columns: found.headers };
}

const itemsTotal = (list) => (Array.isArray(list) ? r2(list.reduce((a, x) => a + (Number(x.amount) || 0), 0)) : null);

// What importing would change for this person: { changes: [{ month, field, from, to }], unchanged, warnings }
export function diffImport(state, model, personId, parsed) {
  const changes = [];
  const warnings = [];
  let unchanged = 0;
  for (const row of parsed.rows || []) {
    const entry = model.byKey[row.month]?.people.find((e) => e.personId === personId);
    if (!entry) {
      if (Object.keys(row.values).length) warnings.push(`${row.month} is outside the months in the app (or they weren't taking part then); skipped`);
      continue;
    }
    const stored = state.months?.[row.month]?.people?.[personId] || {};
    for (const [field, raw] of Object.entries(row.values)) {
      const items = Array.isArray(raw) ? raw : null;
      const to = items ? itemsTotal(items) : raw;
      let from;
      if (field === 'extraIncome' || field === 'extraSpending') from = itemsTotal(stored[field]);
      else from = stored[field] ?? null;
      let same = field === 'note' ? (from || '') === to : from !== null && Math.abs(from - to) < 0.005;
      // same total but different lines (e.g. a description changed)
      if (same && items) same = JSON.stringify(items.map((x) => [x.label, x.amount])) === JSON.stringify((stored[field] || []).map((x) => [x.label, x.amount]));
      if (same) unchanged++;
      else changes.push({ month: row.month, field, from, to, ...(items ? { items } : {}) });
    }
  }
  return { changes, unchanged, warnings };
}
